/**
 * Email Verification Page
 *
 * Handles email verification via token from URL.
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { FiMail, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi'
import { emailVerificationApi } from '../../api'
import { Button } from '../../components/ui/button'

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token'

export function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<VerificationStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [countdown, setCountdown] = useState(0)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setStatus('no-token')
      return
    }

    // Verify the token
    emailVerificationApi.verifyEmail(token).then(res => {
      if (res.success && res.data.verified) {
        setStatus('success')
      } else {
        setStatus('error')
        setError(res.error || 'Verification failed')
      }
    })
  }, [token])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleResend = async () => {
    setCountdown(60)
    const res = await emailVerificationApi.resendVerificationEmail()
    if (!res.success) {
      setError(res.error || 'Failed to resend')
    }
  }

  return (
    <div className="min-h-screen bg-background-secondary flex items-center justify-center p-4">
      <div className="bg-background rounded-[var(--radius-card)] p-8 w-full max-w-[400px] shadow-lg text-center">
        {status === 'loading' && (
          <div className="flex flex-col gap-4 items-center">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <h2 className="text-lg font-semibold text-foreground">
              Verifying your email...
            </h2>
            <p className="text-foreground-muted">Please wait while we verify your email address.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col gap-4 items-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <FiCheck size={40} className="text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              Email Verified!
            </h2>
            <p className="text-foreground-muted">
              Your email has been successfully verified. You can now access all features.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col gap-4 items-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <FiX size={40} className="text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              Verification Failed
            </h2>
            <p className="text-foreground-muted">
              {error || 'The verification link is invalid or has expired.'}
            </p>
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={countdown > 0}
              className="w-full"
            >
              <FiRefreshCw className="mr-2" />
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Verification Email'}
            </Button>
            <RouterLink to="/login">
              <Button variant="ghost" size="sm">
                Back to Login
              </Button>
            </RouterLink>
          </div>
        )}

        {status === 'no-token' && (
          <div className="flex flex-col gap-4 items-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <FiMail size={40} className="text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">
              Check Your Email
            </h2>
            <p className="text-foreground-muted">
              We've sent a verification link to your email address. Click the link to verify
              your account.
            </p>
            <div className="bg-primary/5 p-4 rounded-[var(--radius-button)] w-full">
              <p className="text-sm text-foreground-muted">
                Didn't receive the email? Check your spam folder or request a new verification link below.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleResend}
              disabled={countdown > 0}
              className="w-full"
            >
              <FiRefreshCw className="mr-2" />
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Verification Email'}
            </Button>
            <RouterLink to="/">
              <Button variant="ghost" size="sm">
                Continue to Dashboard
              </Button>
            </RouterLink>
          </div>
        )}
      </div>
    </div>
  )
}
